import { bootup } from "@app/bootup";
import { createLog } from "@app/debug";
import {
  type Callable,
  type Operation,
  PERSIST_LOADER_ID,
  createBatchMdw,
  createLocalStorageAdapter,
  createPersistor,
  parallel,
  persistStoreMdw,
  take,
} from "@app/fx";
import {
  type WebState,
  schema,
  initialState as schemaInitialState,
} from "@app/schema";
import { getCookie } from "@app/ui/shared/cookie-notice";
import { createStore } from "starfx";
import { rootEntities, tasks } from "./packages";

const log = createLog("fx");

/**
 * Map of table names to sensitive fields that should be cleared before persisting to localStorage
 */
const SENSITIVE_FIELDS: Record<string, string[]> = {
  databases: ["connectionUrl"],
  databaseCredentials: ["connectionUrl"],
  endpoints: ["tokenHeader"],
  llmKeys: ["token"],
  logDrains: ["drainPassword", "loggingToken", "url"],
  metricDrains: [
    "drainConfiguration",
    "agggregatorCaCertificate",
    "aggregatorCaPrivateKeyBlob",
    "aggregatorHost",
  ],
};

const SANITIZED_NULL_VALUES = ["drainConfiguration"];

function getSanitizedValue(fieldName: string): null | "" {
  if (SANITIZED_NULL_VALUES.includes(fieldName)) {
    return null;
  }
  return "";
}

function sanitizeEntity(
  entity: Record<string, any>,
  sensitiveFields: string[],
): Record<string, any> {
  const sanitized = { ...entity };

  for (const fieldName of sensitiveFields) {
    if (fieldName in sanitized) {
      sanitized[fieldName] = getSanitizedValue(fieldName);
    }
  }

  return sanitized;
}

function sanitizeTable(
  table: Record<string, any>,
  sensitiveFields: string[],
): Record<string, any> {
  const sanitizedTable: Record<string, any> = {};

  for (const [id, entity] of Object.entries(table)) {
    sanitizedTable[id] = sanitizeEntity(entity, sensitiveFields);
  }

  return sanitizedTable;
}

export function createSanitizingLocalStorageAdapter() {
  const baseAdapter = createLocalStorageAdapter();

  return {
    ...baseAdapter,
    setItem: (key: string, state: Partial<WebState>) => {
      const sanitizedState = { ...state };

      for (const [tableName, sensitiveFields] of Object.entries(
        SENSITIVE_FIELDS,
      )) {
        const table = state[tableName as keyof WebState];

        if (!table || typeof table !== "object") {
          continue;
        }

        // Sanitize the table and update state
        const sanitizedTable = sanitizeTable(
          table as Record<string, any>,
          sensitiveFields,
        );

        sanitizedState[tableName as keyof WebState] = sanitizedTable as any;
      }

      return baseAdapter.setItem(key, sanitizedState);
    },
  };
}

export function setupStore({
  logs = true,
  initialState = {},
}: { logs?: boolean; initialState?: Partial<WebState> }) {
  const enableEnhancedCaching =
    getCookie("aptible-enable-local-storage-caching") === "true";

  const defaultAllowList: (keyof WebState)[] = [
    "theme",
    "nav",
    "redirectPath",
    "feedback",
    "resourceStats",
    "notices",
  ];

  const enhancedAllowList: (keyof WebState)[] = [
    ...defaultAllowList,
    // Deploy Resources
    "apps",
    "costs",
    "databases",
    "environments",
    "stacks",
    "services",
    "endpoints",
    "logDrains",
    "metricDrains",
    "images",
    "llmKeys",
    "persistentDisks",
  ];

  const persistor = createPersistor<WebState>({
    adapter: enableEnhancedCaching
      ? createSanitizingLocalStorageAdapter()
      : createLocalStorageAdapter(),
    allowlist: enableEnhancedCaching ? enhancedAllowList : defaultAllowList,
  });

  const store = createStore<WebState>({
    initialState: {
      ...schemaInitialState,
      entities: rootEntities,
      ...initialState,
    },
    middleware: [createBatchMdw(queueMicrotask), persistStoreMdw(persistor)],
  });

  const tsks: Callable<unknown>[] = [];
  if (logs) {
    tsks.push(function* logActions(): Operation<void> {
      while (true) {
        const action = yield* take("*");
        if (action.type === "store") {
          continue;
        }
        log(action);
      }
    });
  }
  tsks.push(...tasks);

  store.run(function* (): Operation<void> {
    yield* persistor.rehydrate();
    yield* schema.update(schema.loaders.success({ id: PERSIST_LOADER_ID }));
    const group = yield* parallel(tsks);
    yield* bootup.run();
    yield* group;
  });

  return store;
}

// persistor makes things more complicated for our tests so we are deliberately
// choosing to not include it for testing.
export function setupTestStore(initialState: Partial<WebState>) {
  const store = createStore<WebState>({
    initialState: {
      ...schemaInitialState,
      entities: rootEntities,
      // Each test store needs its own AbortController. schemaInitialState.signal
      // is a module-level singleton; if a prior test's watchSignal aborted it,
      // the aborter middleware would immediately abort every request in this store.
      signal: new AbortController(),
      ...initialState,
    },
    middleware: [createBatchMdw(queueMicrotask)],
  });

  store.run(function* (): Operation<void> {
    const group = yield* parallel(tasks);
    yield* group;
  });

  return store;
}
